/**
 * ImpersonationBanner — System Admin impersonation indicator.
 *
 * Story 76 — When a System Admin is impersonating a Company, a red/orange
 * banner displays at the top of the page with the company name and a
 * "Stop Impersonating" button. This component should be mounted globally
 * (e.g., in App.tsx or main.tsx) so it appears on every page during
 * impersonation.
 */

import { useEffect, useState, type ReactElement } from 'react';
import { adminApi } from '../../state/admin/api.ts';

interface ImpersonationState {
  is_impersonating: boolean;
  company_name?: string;
  admin_name?: string;
}

export function ImpersonationBanner(): ReactElement | null {
  const [state, setState] = useState<ImpersonationState | null>(null);
  const [stopping, setStopping] = useState(false);

  useEffect(() => {
    // Check impersonation state from session.
    // This would typically come from a /api/admin/me or similar endpoint
    // that returns current session state including impersonation flags.
    // For now, we'll rely on the whoami endpoint to be enhanced with this info.
    void (async () => {
      const result = await adminApi.whoami();
      if (result.ok) {
        // The whoami response should include impersonation state
        // This is a placeholder — actual implementation depends on backend response shape
        // For now, we check if there's impersonation context in the session
        // via a separate call or by enhancing whoami
      }
    })();
  }, []);

  const handleStop = (): void => {
    setStopping(true);
    void (async () => {
      const result = await adminApi.stopImpersonation();
      if (result.ok) {
        setState(null);
        // Optionally reload the page to clear impersonation context
        window.location.reload();
      } else {
        setStopping(false);
      }
    })();
  };

  if (!state || !state.is_impersonating) {
    return null;
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-red-500 text-white px-4 py-3">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="font-semibold">
            Impersonating {state.company_name}
            {state.admin_name && ` as System Admin ${state.admin_name}`}
          </span>
        </div>
        <button
          onClick={handleStop}
          disabled={stopping}
          className="bg-white text-red-500 px-3 py-1 rounded font-medium hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {stopping ? 'Stopping...' : 'Stop Impersonating'}
        </button>
      </div>
    </div>
  );
}
